import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, IconButton, TextField } from '@printtat/ui';
import { Copy, ShieldCheck, ShieldX } from 'lucide-react';
import type {
  MfaConfirmResponse,
  MfaSetupResponse,
} from '@printtat/shared';
import { apiJson } from '@/lib/apiFetch';
import { useAuth } from '@/contexts/AuthContext';
import { TrustedDevicesList } from '@/components/TrustedDevicesList';

type Stage =
  | { kind: 'idle' }
  | { kind: 'setting-up'; setup: MfaSetupResponse; code: string }
  | { kind: 'showing-codes'; codes: string[] }
  | { kind: 'disabling'; code: string };

// Standalone admin setup page. Reachable by any signed-in admin (whether or
// not they already have MFA enabled). The AdminGuard sends admins without
// MFA here on every render; once they enable it, the guard lets them
// through to /manage/* routes.
export const MfaSetupPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const totpEnabled = user?.totpEnabled ?? false;
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const beginSetup = async () => {
    setError(null);
    setBusy(true);
    try {
      const setup = await apiJson<MfaSetupResponse>('/auth/2fa/setup', {
        method: 'POST',
      });
      setStage({ kind: 'setting-up', setup, code: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start MFA setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (stage.kind !== 'setting-up') return;
    setError(null);
    setBusy(true);
    try {
      const result = await apiJson<MfaConfirmResponse>('/auth/2fa/confirm', {
        method: 'POST',
        body: { code: stage.code },
      });
      setStage({ kind: 'showing-codes', codes: result.backupCodes });
      await refreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const submitDisable = async () => {
    if (stage.kind !== 'disabling') return;
    setError(null);
    setBusy(true);
    try {
      await apiJson('/auth/2fa/disable', {
        method: 'POST',
        body: { code: stage.code },
      });
      setStage({ kind: 'idle' });
      await refreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-md mx-auto space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div className="flex items-center gap-2">
            {totpEnabled ? (
              <ShieldCheck className="w-5 h-5 text-green-600" />
            ) : (
              <ShieldX className="w-5 h-5 text-amber-500" />
            )}
            <h1 className="text-base font-semibold text-gray-900">
              Two-Factor Authentication
            </h1>
            <Badge color={totpEnabled ? 'green' : 'amber'} variant="soft">
              {totpEnabled ? 'Enabled' : 'Required'}
            </Badge>
          </div>

          {!totpEnabled && (
            <p className="text-sm text-gray-600">
              Admin accounts are required to enable 2FA before accessing the
              management panel.
            </p>
          )}

          {stage.kind === 'idle' && !totpEnabled && (
            <Button onClick={beginSetup} disabled={busy} className="w-full">
              Enable 2FA now
            </Button>
          )}

          {stage.kind === 'idle' && totpEnabled && (
            <>
              <p className="text-sm text-gray-600">
                Your account is protected with 2FA. You can finish setting up
                the rest of the admin or disable 2FA below.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => navigate('/')}>Continue to admin</Button>
                <Button
                  variant="outline"
                  onClick={() => setStage({ kind: 'disabling', code: '' })}
                >
                  Disable 2FA
                </Button>
              </div>
              <div className="mt-5 pt-5 border-t border-gray-100">
                <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                  Trusted Devices
                </h2>
                <TrustedDevicesList />
              </div>
            </>
          )}

          {stage.kind === 'setting-up' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Scan this QR with any TOTP authenticator app, then enter the
                6-digit code it shows.
              </p>
              <div className="flex flex-col items-center gap-3">
                <img
                  src={stage.setup.qrCodeDataUrl}
                  alt="2FA QR code"
                  className="w-48 h-48 border border-gray-200 rounded"
                />
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <span>Or enter manually:</span>
                  <code className="bg-gray-100 px-2 py-1 rounded font-mono">
                    {stage.setup.secret}
                  </code>
                  <IconButton
                    type="button"
                    size="xs"
                    variant="ghost"
                    onClick={() =>
                      navigator.clipboard.writeText(stage.setup.secret)
                    }
                    aria-label="Copy secret"
                  >
                    <Copy size={14} />
                  </IconButton>
                </div>
              </div>
              <TextField
                value={stage.code}
                onChange={(e) =>
                  setStage({
                    ...stage,
                    code: e.target.value.replace(/\D/g, '').slice(0, 6),
                  })
                }
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
              />
              {error && <Alert variant="error">{error}</Alert>}
              <div className="flex gap-2">
                <Button
                  onClick={confirmSetup}
                  disabled={busy || stage.code.length !== 6}
                >
                  {busy ? 'Verifying…' : 'Confirm'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStage({ kind: 'idle' })}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {stage.kind === 'showing-codes' && (
            <div className="space-y-3">
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-amber-900">
                  Save these backup codes
                </p>
                <p className="text-xs text-amber-800">
                  Each code can be used once if you lose access to your
                  authenticator. They will NOT be shown again.
                </p>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-white rounded p-3 border border-amber-200">
                  {stage.codes.map((c) => (
                    <div key={c}>{c}</div>
                  ))}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    navigator.clipboard.writeText(stage.codes.join('\n'))
                  }
                >
                  <Copy size={14} className="mr-1.5" />
                  Copy all
                </Button>
              </div>
              <Button onClick={() => navigate('/')} className="w-full">
                I&apos;ve saved them — continue to admin
              </Button>
            </div>
          )}

          {stage.kind === 'disabling' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Enter a 6-digit code from your authenticator (or a backup
                code) to confirm.
              </p>
              <TextField
                value={stage.code}
                onChange={(e) =>
                  setStage({ ...stage, code: e.target.value.trim() })
                }
                placeholder="123456 or xxxx-xxxx"
                autoComplete="one-time-code"
                autoFocus
              />
              {error && <Alert variant="error">{error}</Alert>}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={submitDisable}
                  disabled={busy || stage.code.length < 6}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  {busy ? 'Disabling…' : 'Disable 2FA'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStage({ kind: 'idle' })}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
